import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const ToolDetail = () => {
  const { id } = useParams();
  const [tool, setTool] = useState(null);
  const [machines, setMachines] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/tools/${id}`);
        setTool(res.data);
        const m = await axios.get(`${API}/machines`).catch(() => ({ data: [] }));
        setMachines(m.data || []);
      } catch (e) {
        console.error('Failed to load tool detail', e);
      }
    };
    load();
  }, [id]);

  const machine = useMemo(() => {
    const required = ['axis_count', 'motor_class', 'spindle_type_manual', 'controller_board', 'rigidity'];
    const isSet = (v) => typeof v === 'string' && v.trim() !== '' && v.toLowerCase() !== 'unknown';
    return machines.find((m) => required.every((f) => isSet(m?.[f] || ''))) || machines[0] || null;
  }, [machines]);

  const compatibility = useMemo(() => {
    if (!tool || !machine) return null;

    const spindle = machine.spindle_type_manual || machine.spindle_type_inferred || 'unknown';
    const machineMaxRpm = typeof machine.max_rpm === 'number' ? machine.max_rpm : null;
    const toolRpmMin = tool.recommended_rpm_range?.min ?? null;

    const maxDiaAllowed = (() => {
      if (spindle === 'trim_router') return 8;
      if (spindle === 'small_water_cooled') return 12;
      if (spindle === 'medium_spindle') return 16;
      if (spindle === 'high_power_spindle') return 25;
      return 8;
    })();

    const minDiameter = Math.min(...(tool.diameters_mm || [999]));
    const tooLarge = minDiameter > maxDiaAllowed;

    const requiresHighRigidity = (tool.tags || []).includes('high_rigidity');
    const rigidity = (machine.rigidity || 'unknown').toLowerCase();
    const lowRigidity = rigidity === 'light' || rigidity === 'unknown';

    const rpmMismatch = machineMaxRpm && toolRpmMin ? machineMaxRpm < toolRpmMin : false;

    if (tooLarge) {
      return { status: 'warn', message: `Not suitable: tool sizes start above ${maxDiaAllowed}mm for your spindle.` };
    }

    if ((requiresHighRigidity && lowRigidity) || rpmMismatch) {
      const reasons = [];
      if (requiresHighRigidity && lowRigidity) reasons.push('requires higher rigidity');
      if (rpmMismatch) reasons.push(`needs ≥${toolRpmMin} RPM`);
      return { status: 'warn', message: `⚠ ${reasons.join(' and ')} than your machine supports.` };
    }

    return { status: 'ok', message: '✔ Suitable for your machine' };
  }, [tool, machine]);

  const calculatorLink = useMemo(() => {
    if (!tool) return '/tools/feeds-speeds';

    const d = (tool.diameters_mm || [])[0];
    const fl = (tool.flute_count || [])[0];

    // Map database categories into Calculator tool_type / bit_direction.
    const cat = (tool.category || '').toLowerCase();
    let tool_type = 'end_mill';
    let bit_direction = 'upcut';

    if (cat.includes('v-bit')) tool_type = 'v_bit';
    else if (cat.includes('ball')) tool_type = 'ball_nose';
    else if (cat.includes('surfacing')) tool_type = 'surfacing';
    else if (cat.includes('compression')) {
      tool_type = 'spiral_compression';
      bit_direction = 'compression';
    } else if (cat.includes('downcut')) {
      tool_type = 'spiral_downcut';
      bit_direction = 'downcut';
    } else if (cat.includes('upcut')) {
      tool_type = 'spiral_upcut';
      bit_direction = 'upcut';
    }

    const chipMin = tool.recommended_chipload?.min;
    const rpmMin = tool.recommended_rpm_range?.min;

    const params = new URLSearchParams();
    if (d) params.set('tool_diameter', String(d));
    if (fl) params.set('flute_count', String(fl));
    if (tool_type) params.set('tool_type', tool_type);
    if (bit_direction) params.set('bit_direction', bit_direction);
    if (chipMin !== undefined && chipMin !== null) params.set('desired_chip_load', String(chipMin));
    if (rpmMin !== undefined && rpmMin !== null) params.set('spindle_rpm', String(rpmMin));

    return `/tools/feeds-speeds?${params.toString()}`;
  }, [tool]);

  if (!tool) {
    return <div className="text-sm text-[var(--text-secondary)]">Loading tool…</div>;
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="tool-detail">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="panel-title">Knowledge Base / Cutting Tools Database</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">{tool.name}</h1>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant="secondary">{tool.category}</Badge>
            <Badge variant="secondary">{tool.level}</Badge>
            {(tool.tags || []).slice(0, 4).map((t) => (
              <Badge key={t} variant="outline">{t}</Badge>
            ))}
          </div>
          {compatibility && (
            <div className={compatibility.status === 'ok' ? 'text-emerald-300 text-sm mt-2' : 'text-amber-300 text-sm mt-2'}>
              Compatibility: {compatibility.message}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-700 text-slate-200" asChild>
            <Link to="/knowledge/tools">Back</Link>
          </Button>
          <Button className="btn btn-primary" asChild>
            <Link to={calculatorLink}>Use in Calculator</Link>
          </Button>
        </div>
      </div>

      <Card className="bg-black/60 border-[#3a3a3a]">
        <CardHeader>
          <CardTitle className="text-base text-white">Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-[var(--text-secondary)]">
          <div>
            <div className="text-slate-200 text-xs uppercase font-semibold">Description</div>
            <div className="mt-1 whitespace-pre-wrap">{tool.descriptions?.usage || '—'}</div>
          </div>
          <div>
            <div className="text-slate-200 text-xs uppercase font-semibold">Benefits</div>
            <div className="mt-1 whitespace-pre-wrap">{tool.descriptions?.benefits || '—'}</div>
          </div>
          <div>
            <div className="text-slate-200 text-xs uppercase font-semibold">Limitations</div>
            <div className="mt-1 whitespace-pre-wrap">{tool.descriptions?.limitations || '—'}</div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-black/60 border-[#3a3a3a]">
          <CardHeader>
            <CardTitle className="text-base text-white">Specifications</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-[var(--text-secondary)] space-y-2">
            <div>Diameters (mm): <span className="text-slate-200">{(tool.diameters_mm || []).join(', ') || '—'}</span></div>
            <div>Flutes: <span className="text-slate-200">{(tool.flute_count || []).join(', ') || '—'}</span></div>
            <div>Suitable materials: <span className="text-slate-200">{(tool.materials || []).join(', ') || '—'}</span></div>
            <div>Operations supported: <span className="text-slate-200">{(tool.operation_types || []).join(', ') || '—'}</span></div>
          </CardContent>
        </Card>

        <Card className="bg-black/60 border-[#3a3a3a]">
          <CardHeader>
            <CardTitle className="text-base text-white">Safety & Warnings</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-[var(--text-secondary)] space-y-2">
            {(tool.warnings || []).length ? (
              <ul className="list-disc list-inside space-y-1">
                {tool.warnings.map((w, idx) => (
                  <li key={idx}>{w}</li>
                ))}
              </ul>
            ) : (
              <div>—</div>
            )}

            {tool.recommended_chipload && (
              <div className="mt-3">
                <div className="text-slate-200 text-xs uppercase font-semibold">Recommended chipload (mm/tooth)</div>
                <div className="mt-1">{tool.recommended_chipload.min} – {tool.recommended_chipload.max}</div>
              </div>
            )}

            {tool.recommended_rpm_range && (
              <div className="mt-3">
                <div className="text-slate-200 text-xs uppercase font-semibold">Recommended RPM range</div>
                <div className="mt-1">{tool.recommended_rpm_range.min} – {tool.recommended_rpm_range.max}</div>
              </div>
            )}

            {machine && (
              <div className="mt-3 text-xs text-[var(--text-secondary)]">
                Machine reference: <span className="text-slate-200">{machine.name || 'Machine'}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ToolDetail;
